import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormArray,
  Validators,
  Validator,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MatDialog } from '@angular/material/dialog';
import { greaterThanValidator } from '@helpers/Validators';
import {
  ItemPrepGroupDialogComponent,
  ItemPrepGroupDialogData,
  ItemPrepGroupDialogResult,
} from '@components/item-prep-group-dialog/1.0';
import { IsEmptyService } from '@services/utilities/1.0/is-empty.service';
import { NgIf } from '@angular/common';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../../directives/integers-only/1.0/txp-integers-only.directive';
import { ButtonComponent } from '../../button/1.0/button.component';
@Component({
  selector: 'txp-item-prep-groups-template-form',
  templateUrl: './item-prep-groups-template-form.component.html',
  styleUrls: ['./item-prep-groups-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemPrepGroupsTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ItemPrepGroupsTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatFormField,
    MatLabel,
    MatInput,
    TxpIntegersOnlyDirective,
    MatError,
    ButtonComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class ItemPrepGroupsTemplateFormComponent implements ControlValueAccessor, Validator {
  buttonStyle = ButtonStyle; // expose enum to template
  brandId: string;
  loadingColor = LoadingColor;
  loadingType = LoadingType;
  buttonSize = ButtonSize;
  displayColumns = ['groupName', 'rangeStart', 'rangeEnd', 'prepTime', 'action'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  form: UntypedFormGroup;
  onTouched: any;
  onChangeFn: any;
  defaultFormValue: any;
  subscription: Subscription;
  disabled: false;

  private unsubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {
    this.form = this.formBuilder.group({
      groups: new UntypedFormArray([]),
    });
    this.defaultFormValue = this.form.value;
    this.brandId = sessionStorage.getItem('brand_id');
  }

  writeValue(obj): void {
    const groupsControl = this.form.get('groups') as UntypedFormArray;

    const newList = [];

    this.dataSource.data = [];

    // clear array
    groupsControl.clear();

    // add form controls for each item
    const controls: UntypedFormGroup[] = [];

    if (!IsEmptyService.isEmpty(obj)) {
      for (const groupData of obj) {
        controls.push(this.createGroupForm(groupData));
        newList.push(groupData);
      }
    }

    // set new FormArray all at once to avoid unnecessary change events
    this.form.setControl('groups', new UntypedFormArray(controls));
    this.dataSource.data = newList;
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      this.onChange(data);
    });
  }

  onChange(data) {
    this.onChangeFn(data.groups);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  deletePrepGroup(group) {
    const groupsControl = this.form.get('groups') as UntypedFormArray;

    this.dataSource.data = this.dataSource.data.filter((item) => item !== group);

    groupsControl.removeAt(groupsControl.controls.indexOf(group));
  }

  getGroupForm(index): any {
    const groupControl = this.form.get('groups') as UntypedFormArray;

    return groupControl.controls[index] as UntypedFormControl;
  }

  createGroupForm(groupData?) {
    const group = this.formBuilder.group({
      name: new UntypedFormControl(groupData?.name || '', [Validators.required]),
      items: new UntypedFormControl(groupData?.items || []),
      rangeStart: new UntypedFormControl(
        typeof groupData?.rangeStart !== 'undefined' ? groupData?.rangeStart : '',
        [Validators.required],
      ),
      rangeEnd: new UntypedFormControl(
        typeof groupData?.rangeEnd !== 'undefined' ? groupData?.rangeEnd : '',
        [Validators.required],
      ),
      prepTime: new UntypedFormControl(
        typeof groupData?.prepTime !== 'undefined' ? groupData?.prepTime : '',
        [Validators.required],
      ),
    });
    // Add validators that depend on other controls
    const rangeStartField = group.get('rangeStart');
    const rangeEndField = group.get('rangeEnd');
    const prepTimeField = group.get('prepTime');

    // Need to make sure to include current validator function too
    rangeEndField.setValidators([rangeStartField.validator, greaterThanValidator(rangeStartField)]);

    group.valueChanges.subscribe(() => {
      rangeStartField.updateValueAndValidity({ emitEvent: false });
      rangeEndField.updateValueAndValidity({ emitEvent: false });
      prepTimeField.updateValueAndValidity({ emitEvent: false });
    });

    return group;
  }

  createDialogData(group?): ItemPrepGroupDialogData {
    return {
      name: group?.name || '',
      items: group?.items ? group.items.map((id) => id) : [],
      translations: {
        title: `${this.translateService.instant(
          'store.dynamicPrepTime.prepGroupModal.manageItemPrepGroup',
        )}`,
        type: this.translateService.instant('store.dynamicPrepTime.prepGroupModal.products'),
        group: this.translateService.instant(
          'store.dynamicPrepTime.prepGroupModal.itemPrepGroupName',
        ),
        listTitle: this.translateService.instant('store.dynamicPrepTime.prepGroupModal.product'),
      },
    };
  }

  onCreateNewGroup() {
    const dialogData = this.createDialogData();
    const dialogRef = this.dialog.open(ItemPrepGroupDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: ItemPrepGroupDialogResult) => {
      // result undefined means it was cancelled
      if (result) {
        const groupsControl = this.form.get('groups') as UntypedFormArray;
        const newGroup = {
          items: result.items.map((d) => ({ sku: d.catalogEntryId, name: d.name })),
          name: result.name,
          rangeStart: '',
          rangeEnd: '',
          prepTime: '',
        };

        this.dataSource.data = [...this.dataSource.data, newGroup];

        groupsControl.push(this.createGroupForm(newGroup));
      }
    });
  }

  onEditGroup(group) {
    const dialogData = this.createDialogData(group);
    const dialogRef = this.dialog.open(ItemPrepGroupDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: ItemPrepGroupDialogResult) => {
      // result undefined means it was cancelled
      if (result) {
        const groupsControl = this.form.get('groups') as UntypedFormArray;
        const groupIndex = this.dataSource.data.findIndex((gr) => gr === group);
        const existingGroup = groupsControl.controls[groupIndex] as UntypedFormGroup;
        const newGroupData = {
          ...existingGroup.value,
          items: result.items.map((d) => ({ sku: d.catalogEntryId, name: d.name })),
          name: result.name,
        };

        this.dataSource.data = this.dataSource.data.map((gr, index) =>
          groupIndex === index ? newGroupData : gr,
        );

        groupsControl.removeAt(groupIndex);
        groupsControl.insert(groupIndex, this.createGroupForm(newGroupData));
      }
    });
  }

  validate(): ValidationErrors {
    return !this.form.valid
      ? {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        }
      : undefined;
  }
}
